import {
  PARTIES_HELP_ARTICLE,
  PARTY_EXEMPT_HELP_ARTICLE,
} from "@/lib/help-parties";
import type { HelpArticle, HelpCategory } from "@/lib/registrant-help/types";

/** Categories shared by government and operator Help Centers. */
export const STAFF_HELP_CATEGORIES: HelpCategory[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary: "Sign in, find your way around, and open Help.",
  },
  {
    id: "registrations",
    title: "Registrations",
    summary:
      "Registration lists, details, Needs Attention, compliance issues, and comments.",
  },
];

export const STAFF_SHARED_HELP_ARTICLES: HelpArticle[] = [
  PARTIES_HELP_ARTICLE,
  PARTY_EXEMPT_HELP_ARTICLE,
  {
    id: "registrations",
    title: "Registrations",
    summary:
      "The registrations list, filters, Needs Attention column, and opening a property.",
    category: "registrations",
    keywords: [
      "registrations",
      "licenses",
      "list",
      "filter",
      "needs attention",
      "column",
    ],
    related: [
      "needs-attention",
      "compliance-issues",
      "registration-details",
      "dashboard",
    ],
    sections: [
      {
        heading: "What you see",
        blocks: [
          {
            type: "p",
            text: "The Registrations section lists properties in your scope. Use jurisdiction, license type, status, and other filters to narrow the table. Click a row to open registration details in a side sheet.",
          },
        ],
      },
      {
        heading: "Needs Attention column",
        blocks: [
          {
            type: "p",
            text: "The Needs Attention column shows whether a registration needs follow-up for a late renewal, an open compliance issue, or both. Staff labels can include Ready for Check when the registrant has reported a compliance issue resolved. See Needs Attention for the full inclusion rules.",
          },
        ],
      },
      {
        heading: "Upload",
        blocks: [
          {
            type: "p",
            text: "When available, registration upload imports a spreadsheet of licenses. See Registration upload for how rows, organizations, and additional questions are processed.",
          },
        ],
      },
    ],
  },
  {
    id: "needs-attention",
    title: "Needs Attention",
    summary:
      "Which registrations need attention: late renewals (30–90 days, or 30 or more days after historical data is cleaned) and open compliance issues, plus how emails work.",
    category: "registrations",
    keywords: [
      "needs attention",
      "late renewal",
      "compliance",
      "email",
      "30",
      "90",
      "exempt",
      "historical data cleaned",
    ],
    related: [
      "compliance-issues",
      "registrations",
      "dashboard",
      "party-exempt",
    ],
    sections: [
      {
        heading: "What counts as Needs Attention",
        blocks: [
          {
            type: "p",
            text: "A registration is marked Needs Attention when either of these is true:",
          },
          {
            type: "ul",
            items: [
              "Late renewal — the renewal date is 30 to 90 days in the past (inclusive), the OMT on the registration is not marked exempt, and the status is not in the excluded set (for example cancelled, rejected, exempt, paid pending review, or de-registration in progress). When Historical Data Cleaned is on for that jurisdiction and license type, the 90-day cap is removed and 30 or more days late still counts.",
              "Active compliance issue — the registration has an open compliance issue or one that is Ready for Check (pending staff clear). Compliance can keep a row in Needs Attention even when the late-renewal rule does not apply.",
            ],
          },
          {
            type: "p",
            text: "Renewals fewer than 30 days late are not in the late-renewal bucket. Renewals more than 90 days late are also left out unless Historical Data Cleaned is on for that jurisdiction and license type. Cancelled registrations are excluded from registrant and OMT Needs Attention tabs even if a compliance flag remains. When the OMT party is marked Exempt, late-renewal Needs Attention stays off and registration and late fees due are zero. Registration and renewal are still required. See Exempt.",
          },
        ],
      },
      {
        heading: "Column labels",
        blocks: [
          {
            type: "ul",
            items: [
              "Needs Attention — late renewal only.",
              "Compliance Issue — open compliance issue (staff may also see Ready for Check).",
              "Needs Attention, Compliance Issue — both late renewal and compliance.",
              "No — neither rule applies.",
            ],
          },
          {
            type: "p",
            text: "Registrant and OMT lists do not show the Ready for Check wording in the column; staff portals do when the issue is pending check.",
          },
        ],
      },
      {
        heading: "Emails",
        blocks: [
          {
            type: "ul",
            items: [
              "When a compliance issue is flagged, the organization’s primary registrant receives an email immediately. The subject names the property address when available, and photo attachments on the issue can appear in the message.",
              "Separately, a scheduled Needs Attention email (Monday and Wednesday) lists properties that meet the late-renewal window and/or have an active compliance issue. The email uses the same 30-day start and the same 90-day cap, including licenses older than 90 days only when Historical Data Cleaned is on. Configuration can send that mail to the registrant primary (with a global CC) or to the CC address only.",
            ],
          },
        ],
      },
      {
        heading: "Historical Data Cleaned",
        blocks: [
          {
            type: "p",
            text: "Operators set Historical Data Cleaned on each jurisdiction license type. Existing jurisdiction license types stay off until an operator turns the switch on. A newly created jurisdiction starts on for the license types saved with it. A license type added later to an existing jurisdiction starts off.",
          },
          {
            type: "p",
            text: "Off means late-renewal Needs Attention stops at 90 days. On means every eligible renewal 30 or more days late stays in Needs Attention. The Monday and Wednesday email uses the flag as soon as it is saved. Dashboard payment amounts and Needs Attention for a large jurisdiction stay as they are until the nightly cache refresh. Status exclusions and OMT exemption still apply. The switch is not part of ordinance history.",
          },
        ],
      },
      {
        heading: "Not the same as other lists",
        blocks: [
          {
            type: "p",
            text: "Balance Due, Renewable, and Pending Review (including Notify Hera Registry comments) are separate from Needs Attention. A property can appear in more than one place when different rules apply.",
          },
        ],
      },
    ],
  },
  {
    id: "compliance-issues",
    title: "Compliance issues",
    summary:
      "Flag, track, and clear compliance issues. Registrants are emailed when an issue is raised. Registration status does not change.",
    category: "registrations",
    keywords: [
      "compliance",
      "issue",
      "flag",
      "ready for check",
      "clear",
      "email",
      "comment",
    ],
    related: ["needs-attention", "registration-details", "registrations"],
    sections: [
      {
        heading: "What a compliance issue is",
        blocks: [
          {
            type: "p",
            text: "A compliance issue is a problem on a property that government or Hera Registry staff want the registrant to see and fix. It is separate from an ordinary comment. Flagging an issue does not change the registration status — a compliant registration stays compliant.",
          },
          {
            type: "p",
            text: "Only one active compliance issue can exist on a registration at a time. You cannot flag another until the current one is cleared.",
          },
        ],
      },
      {
        heading: "Flag an issue",
        blocks: [
          {
            type: "ol",
            items: [
              "Open the registration.",
              "In Issues & Comments, choose Add Compliance Issue…",
              "Describe the problem (required). Attach photos or files if they help.",
              "Save.",
            ],
          },
          {
            type: "p",
            text: "The issue is visible to the registrant right away under Issues & Comments, labeled Compliance Issue. The company’s primary account receives an email at once. The registration appears on the registrant’s Compliance Issues tile and shows Compliance Issue on your list.",
          },
        ],
      },
      {
        heading: "How the registrant responds",
        blocks: [
          {
            type: "p",
            text: "The registrant opens the registration (often from Compliance Issues), reads the issue and attachments, then uses Report Issue Resolved… with a description and optional photos. The label changes to Ready for Check. They should not use Notify Hera Registry for that step. The property stays on the registrant’s Compliance Issues tile until your portal clears the issue.",
          },
        ],
      },
      {
        heading: "Check and clear",
        blocks: [
          {
            type: "p",
            text: "When the registrant reports the issue resolved, your Needs Attention column can show Compliance Issue and Ready for Check. Open the registration, review their note and photos, then Clear Compliance Issue if the fix is acceptable.",
          },
          {
            type: "p",
            text: "Only the same party type that raised the issue can clear it. If a government user raised it, a government user clears it. If Hera Registry raised it, Hera Registry clears it. After it is Cleared, you can flag a new issue if needed.",
          },
        ],
      },
      {
        heading: "Comments versus compliance",
        blocks: [
          {
            type: "p",
            text: "Ordinary comments are notes. Add Internal Comment keeps a note with Hera. Post Comment to Registrant shares it and emails the registrant. Registrant Notify Hera Registry comments are a separate pending-review path and do not create a compliance issue.",
          },
        ],
      },
    ],
  },
  {
    id: "registration-details",
    title: "Registration details",
    summary:
      "Property details, questions, payments, Issues & Comments, and compliance actions.",
    category: "registrations",
    keywords: [
      "details",
      "comments",
      "compliance",
      "questions",
      "history",
      "sheet",
    ],
    related: ["compliance-issues", "needs-attention", "registrations"],
    sections: [
      {
        heading: "Opening details",
        blocks: [
          {
            type: "p",
            text: "Click a registration row to open details in a side sheet. You can review status, property information, answers, payment history, and Issues & Comments.",
          },
        ],
      },
      {
        heading: "Issues & Comments",
        blocks: [
          {
            type: "ul",
            items: [
              "Add Comment — leave a note. Add Internal Comment keeps it with Hera. Post Comment to Registrant shares it and emails the registrant.",
              "Add Compliance Issue — flag a problem the registrant must address (see Compliance issues).",
              "Clear Compliance Issue — available when your portal’s party type raised the active issue.",
              "Clear Notification — clears a registrant Notify Hera Registry comment from pending review when applicable.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "registration-upload",
    title: "Registration upload",
    summary:
      "How spreadsheet upload creates or updates registrant accounts, organizations, licenses, and additional questions.",
    category: "registrations",
    keywords: [
      "upload",
      "import",
      "csv",
      "xlsx",
      "spreadsheet",
      "dry run",
      "organization",
    ],
    related: ["registrations", "registration-details"],
    sections: [
      {
        heading: "How registration upload works",
        blocks: [
          {
            type: "p",
            text: "The upload reads your spreadsheet (CSV or XLSX), maps columns to license fields and additional questions, validates each row, and either runs a dry run (preview) or creates licenses and related records. Rows are processed in order; registrant accounts are created or updated as needed, then licenses and their additional question answers are created.",
          },
        ],
      },
      {
        heading: "Registrants and organizations",
        blocks: [
          {
            type: "ul",
            items: [
              "Existing email — non-blank profile fields are not overwritten; blank fields can be filled from the spreadsheet. Company name is updated when provided.",
              "New email — a registrant account is created. No login is created at import time; the person can sign up later and link by email.",
              "Organizations — accounts with the same company name group under one primary; later accounts with that company name become secondaries.",
            ],
          },
        ],
      },
      {
        heading: "Additional questions and dry run",
        blocks: [
          {
            type: "p",
            text: "License types can have additional questions per jurisdiction. Map spreadsheet columns to those questions. Use dry run to preview validation errors before creating records. Address and jurisdiction rules still apply to each row.",
          },
        ],
      },
    ],
  },
  {
    id: "prefiling-deregistration-approval",
    title: "Pre-filing de-registration approval",
    summary:
      "When a pre-filing de-registration is approved immediately, and when a foreclosure case number keeps it in review.",
    category: "registrations",
    keywords: [
      "pre-filing",
      "prefiling",
      "de-registration",
      "deregistration",
      "auto-approved",
      "manual approval",
      "foreclosure case number",
      "no longer in default",
    ],
    related: ["registrations", "registration-details"],
    sections: [
      {
        heading: "Where to set it",
        blocks: [
          {
            type: "p",
            text: "The switch is on the jurisdiction license type, under Registration Approval, and on each ordinance version in the same section. Off is the default and the label reads Pre-filing de-registrations require manual approval. On, the label reads Pre-filing de-registrations are auto-approved.",
          },
          {
            type: "p",
            text: "The switch above it, De-registrations are auto-approved, closes every de-registration reason, including a registration that has a foreclosure case number. The pre-filing switch adds a narrower path when that broader switch is off.",
          },
        ],
      },
      {
        heading: "When a request is approved immediately",
        blocks: [
          {
            type: "p",
            text: "With the pre-filing switch on, Hera approves the de-registration when the de-registration question Reason for the De-Registration Request is answered Property no longer in default (pre-filing only) and the registration has no foreclosure case number.",
          },
          {
            type: "ul",
            items: [
              "Capitalization, extra spaces, and different dash characters still match. A question mark at the end of the question still counts.",
              "A registration or inspection question with the same wording does not count. Only a de-registration question does.",
              "When the jurisdiction charges a de-registration fee, the registrant pays it first. After that payment, the same rules apply. A fee of zero is treated as no fee.",
            ],
          },
        ],
      },
      {
        heading: "When a case number keeps the request in review",
        blocks: [
          {
            type: "p",
            text: "A Foreclosure Case Number already stored on the registration leaves the request in De-registration Pending for staff review, even when the reason is the pre-filing option and the pre-filing switch is on.",
          },
          {
            type: "p",
            text: "A blank answer is not a case number. Placeholders such as N/A, none, and unknown are not a case number either. Those registrations can still be approved immediately.",
          },
        ],
      },
      {
        heading: "What immediate approval does",
        blocks: [
          {
            type: "ul",
            items: [
              "The registration status becomes Cancelled.",
              "Inspections in Paid Pending Review are cancelled. Inspections in any other status stay as they are.",
              "Any other de-registration reason stays in De-registration Pending while only the pre-filing switch is on.",
            ],
          },
        ],
      },
    ],
  },
];
